import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router'
import { pricing } from '@fjalim/shared'
import { auth } from '../api'
import { track } from '../lib/analytics'
import { getDeviceId } from '../lib/device'
import { useAuth } from '../auth/AuthContext'
import { TurnstileCheck } from '../components/TurnstileCheck'
import { useI18n } from '../i18n'
import styles from './Auth.module.css'

const FREE_MINUTES = pricing.signupGrantSeconds / 60

function useSubmit<T>(action: () => Promise<T>) {
  const { errorText } = useI18n()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const run = async (event: FormEvent): Promise<T | undefined> => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      return await action()
    } catch (e) {
      setError(errorText(e))
      return undefined
    } finally {
      setBusy(false)
    }
  }
  return { busy, error, run }
}

function ErrorLine({ message }: { message: string | null }) {
  return (
    <div aria-live="polite">
      {message ? (
        <p className="notice notice-error" role="alert">
          {message}
        </p>
      ) : null}
    </div>
  )
}

export function LoginPage() {
  const { t } = useI18n()
  const { login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const { busy, error, run } = useSubmit(async () => {
    await login(email.trim(), password)
    const from = (location.state as { from?: string } | null)?.from
    navigate(from && from.startsWith('/') ? from : '/', { replace: true })
  })
  return (
    <>
      <h1>{t('auth.login.title')}</h1>
      <p className={styles.lead}>{t('auth.login.lead')}</p>
      <form onSubmit={(e) => void run(e)} noValidate>
        <ErrorLine message={error} />
        <div className="field">
          <label htmlFor="email">{t('auth.field.email')}</label>
          <input id="email" className="input" type="email" autoComplete="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="password">{t('auth.field.password')}</label>
          <input id="password" className="input" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        <div className={styles.links}>
          <Link to="/forgot">{t('auth.forgot.link')}</Link>
        </div>
        <button className={`btn btn-primary ${styles.submit}`} type="submit" disabled={busy || email === '' || password === ''}>
          {t('auth.login.submit')}
        </button>
      </form>
      <p className={styles.alt}>
        {t('auth.login.noAccount')} <Link to="/signup">{t('nav.signup')}</Link>
      </p>
    </>
  )
}

export function SignupPage() {
  const { t, locale } = useI18n()
  const { signup } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [checkEmail, setCheckEmail] = useState(false)
  // The human check, when a Turnstile site key is built in. A token works once, so every attempt asks for a new one.
  const [humanToken, setHumanToken] = useState<string | null>(null)
  const [humanRound, setHumanRound] = useState(0)
  // Load the device id as the page opens: whichever of cookie, localStorage and IndexedDB still has it is written back to all three.
  useEffect(() => {
    void getDeviceId()
  }, [])
  const { busy, error, run } = useSubmit(async () => {
    try {
      const account = await signup({
        email: email.trim(),
        password,
        name: name.trim() || undefined,
        locale,
        ...(humanToken ? { turnstile_token: humanToken } : {}),
      })
      track('signup')
      // Never say whether the email already had an account: without a session the answer is always "check your email".
      if (account) navigate('/', { replace: true })
      else setCheckEmail(true)
    } finally {
      setHumanToken(null)
      setHumanRound((n) => n + 1)
    }
  })
  if (checkEmail) {
    return (
      <>
        <h1>{t('auth.signup.check.title')}</h1>
        <p className="notice notice-ok" role="status">
          {t('auth.signup.check.body')}
        </p>
        <Link className="btn btn-primary" to="/login">
          {t('nav.login')}
        </Link>
      </>
    )
  }
  return (
    <>
      <h1>{t('auth.signup.title')}</h1>
      <p className={styles.lead}>{t('auth.signup.lead', { minutes: FREE_MINUTES })}</p>
      <form onSubmit={(e) => void run(e)} noValidate>
        <ErrorLine message={error} />
        <div className="field">
          <label htmlFor="name">
            {t('auth.field.name')} <span className="hint">({t('common.optional')})</span>
          </label>
          <input id="name" className="input" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="email">{t('auth.field.email')}</label>
          <input id="email" className="input" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="password">{t('auth.field.password')}</label>
          <input id="password" className="input" type="password" autoComplete="new-password" required minLength={10} aria-describedby="pw-hint" value={password} onChange={(e) => setPassword(e.target.value)} />
          <span id="pw-hint" className="hint">
            {t('auth.field.passwordHint')}
          </span>
        </div>
        <TurnstileCheck onToken={setHumanToken} round={humanRound} />
        <button className={`btn btn-primary ${styles.submit}`} type="submit" disabled={busy || email === '' || password.length < 10}>
          {t('auth.signup.submit')}
        </button>
      </form>
      <p className={styles.alt}>
        {t('auth.signup.haveAccount')} <Link to="/login">{t('nav.login')}</Link>
      </p>
    </>
  )
}

export function ForgotPage() {
  const { t } = useI18n()
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const { busy, error, run } = useSubmit(async () => {
    await auth.forgot(email.trim())
    setSent(true)
  })
  return (
    <>
      <h1>{t('auth.forgot.title')}</h1>
      <p className={styles.lead}>{t('auth.forgot.lead')}</p>
      {sent ? (
        <p className="notice notice-ok" role="status">
          {t('auth.forgot.sent')}
        </p>
      ) : (
        <form onSubmit={(e) => void run(e)} noValidate>
          <ErrorLine message={error} />
          <div className="field">
            <label htmlFor="email">{t('auth.field.email')}</label>
            <input id="email" className="input" type="email" autoComplete="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <button className={`btn btn-primary ${styles.submit}`} type="submit" disabled={busy || email === ''}>
            {t('auth.forgot.submit')}
          </button>
        </form>
      )}
      <p className={styles.alt}>
        <Link to="/login">{t('nav.login')}</Link>
      </p>
    </>
  )
}

export function ResetPage() {
  const { t } = useI18n()
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const [password, setPassword] = useState('')
  const [done, setDone] = useState(false)
  const { busy, error, run } = useSubmit(async () => {
    await auth.reset(token, password)
    setDone(true)
  })
  return (
    <>
      <h1>{t('auth.reset.title')}</h1>
      {token === '' ? (
        <p className="notice notice-error" role="alert">
          {t('auth.reset.missing')}
        </p>
      ) : done ? (
        <p className="notice notice-ok" role="status">
          {t('auth.reset.done')}
        </p>
      ) : (
        <form onSubmit={(e) => void run(e)} noValidate>
          <ErrorLine message={error} />
          <div className="field">
            <label htmlFor="password">{t('auth.field.newPassword')}</label>
            <input id="password" className="input" type="password" autoComplete="new-password" required minLength={10} autoFocus aria-describedby="pw-hint" value={password} onChange={(e) => setPassword(e.target.value)} />
            <span id="pw-hint" className="hint">
              {t('auth.field.passwordHint')}
            </span>
          </div>
          <button className={`btn btn-primary ${styles.submit}`} type="submit" disabled={busy || password.length < 10}>
            {t('auth.reset.submit')}
          </button>
        </form>
      )}
      <p className={styles.alt}>
        <Link to={token === '' ? '/forgot' : '/login'}>{token === '' ? t('auth.forgot.title') : t('nav.login')}</Link>
      </p>
    </>
  )
}

type VerifyState = 'working' | 'done' | 'failed' | 'missing'

export function VerifyPage() {
  const { t } = useI18n()
  const { refresh, status } = useAuth()
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const [state, setState] = useState<VerifyState>(token === '' ? 'missing' : 'working')
  const [granted, setGranted] = useState(false)
  const started = useRef(false)

  useEffect(() => {
    if (token === '' || started.current) return
    started.current = true
    auth
      .verify(token)
      .then(async (res) => {
        setGranted(res.signup_grant)
        setState('done')
        track('verify')
        if (status === 'signedIn') await refresh()
      })
      .catch(() => setState('failed'))
  }, [token, refresh, status])

  return (
    <>
      <h1>{t('auth.verify.title')}</h1>
      <div aria-live="polite" className={styles.lead}>
        {state === 'working' ? <p>{t('auth.verify.working')}</p> : null}
        {state === 'missing' ? <p className="notice notice-error">{t('auth.verify.missing')}</p> : null}
        {state === 'failed' ? <p className="notice notice-error">{t('auth.verify.failed')}</p> : null}
        {state === 'done' ? (
          <div className="notice notice-ok">
            <p>
              <strong>{t('auth.verify.done')}</strong>
            </p>
            <p style={{ margin: 0 }}>
              {granted ? t('auth.verify.grant', { minutes: FREE_MINUTES }) : t('auth.verify.noGrant')}
            </p>
          </div>
        ) : null}
      </div>
      <Link className="btn btn-primary" to={status === 'signedIn' ? '/' : '/login'}>
        {status === 'signedIn' ? t('auth.verify.continue') : t('nav.login')}
      </Link>
    </>
  )
}
